import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BackButton from "@/components/BackButton";
import { LayoutDashboard, Users, Package, FileText, Receipt, BookOpen, BarChart3, Settings, LogOut, Menu, DatabaseBackup, PanelLeftClose, PanelLeftOpen, X, ClipboardList } from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/receipts", label: "Receipts", icon: Receipt, testid: "nav-receipts" },
  { to: "/journal", label: "Journal", icon: BookOpen, testid: "nav-journal" },
  { to: "/tasks", label: "Tasks", icon: ClipboardList, testid: "nav-tasks" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", perm: "reports" },
  { to: "/data", label: "Import / Export", icon: DatabaseBackup, testid: "nav-data", perm: "import_export" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings", perms: ["settings", "manage_team"] },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const visibleNav = NAV.filter(n => (n.perm ? can(n.perm) : true) && (n.perms ? n.perms.some(can) : true));
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");
  const toggleCollapsed = () => setCollapsed(v => { localStorage.setItem("sidebar_collapsed", v ? "0" : "1"); return !v; });
  const lgHide = collapsed ? "lg:hidden" : "";

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-slate-50" style={{ "--sb": collapsed ? "4rem" : "15rem" }}>
      {open && <div className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden" data-testid="sidebar-backdrop" onClick={() => setOpen(false)} />}

      <aside data-testid="sidebar" data-collapsed={collapsed ? "1" : "0"}
        className={`bg-slate-900 text-slate-100 w-64 ${collapsed ? "lg:w-16" : "lg:w-60"} fixed inset-y-0 left-0 z-40 flex flex-col transition-[transform,width] duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className={`px-3 py-3 border-b border-slate-800 flex items-center gap-2 ${collapsed ? "lg:justify-center" : ""}`}>
          <div className="w-8 h-8 rounded bg-blue-600 grid place-items-center text-white font-bold shrink-0" data-testid="brand-mark">L</div>
          <div className={`min-w-0 ${lgHide}`}>
            <div className="font-bold tracking-tight text-white text-[15px] truncate" style={{fontFamily: 'Plus Jakarta Sans'}}>LedgerFast</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Invoicing · Ledger</div>
          </div>
          <button className="ml-auto lg:hidden text-slate-400 hover:text-white p-1" onClick={() => setOpen(false)} data-testid="sidebar-close-btn" aria-label="Close menu"><X size={18}/></button>
        </div>
        <nav className="p-2 space-y-0.5 flex-1 overflow-y-auto">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              title={n.label}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 lg:py-2 text-[13px] rounded transition-colors ${collapsed ? "lg:justify-center lg:px-0" : ""} ${isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"}`
              }
            >
              <n.icon size={16} className="shrink-0" />
              <span className={lgHide}>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-800 p-3">
          <div className={`text-[11px] uppercase tracking-wider text-slate-500 mb-1 ${lgHide}`}>Signed in</div>
          <div className={`text-xs text-slate-200 truncate ${lgHide}`} data-testid="signed-in-email">{user?.email}{user?.role !== "admin" && <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-500">member</span>}</div>
          <button data-testid="logout-btn" onClick={handleLogout} title="Sign out" className={`mt-2 w-full flex items-center gap-2 text-xs text-slate-300 hover:text-white px-2 py-1.5 rounded hover:bg-slate-800 ${collapsed ? "lg:justify-center lg:px-0" : ""}`}>
            <LogOut size={14} className="shrink-0"/> <span className={lgHide}>Sign out</span>
          </button>
          <button data-testid="sidebar-collapse-btn" onClick={toggleCollapsed} title={collapsed ? "Expand sidebar" : "Collapse sidebar"} className={`mt-1 w-full hidden lg:flex items-center gap-2 text-xs text-slate-400 hover:text-white px-2 py-1.5 rounded hover:bg-slate-800 ${collapsed ? "lg:justify-center lg:px-0" : ""}`}>
            {collapsed ? <PanelLeftOpen size={14} className="shrink-0"/> : <PanelLeftClose size={14} className="shrink-0"/>} <span className={lgHide}>Collapse</span>
          </button>
        </div>
      </aside>

      <div className={`flex-1 ${collapsed ? "lg:ml-16" : "lg:ml-60"} flex flex-col min-w-0 transition-[margin] duration-200`}>
        <header className="bg-white border-b border-slate-200 px-3 sm:px-4 py-2.5 sm:py-3 sticky top-0 z-20 flex items-center gap-3">
          <button className="lg:hidden text-slate-700 p-1.5 -ml-1.5 rounded hover:bg-slate-100" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-btn" aria-label="Open menu"><Menu size={22}/></button>
          <button className="hidden lg:inline-flex text-slate-500 hover:text-slate-900 p-1.5 -ml-1.5 rounded hover:bg-slate-100" onClick={toggleCollapsed} data-testid="header-collapse-btn" title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            {collapsed ? <PanelLeftOpen size={18}/> : <PanelLeftClose size={18}/>}
          </button>
          {pathname !== "/dashboard" && <BackButton testid="header-back-btn" fallback="/dashboard" className="-ml-1" />}
          <div className="text-sm text-slate-500 hidden sm:block">Welcome back,</div>
          <div className="text-sm font-semibold text-slate-900 truncate">{user?.name || user?.email}</div>
        </header>
        <main className="p-3 sm:p-6 lg:p-8 flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
